import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import { api } from "../lib/api";
import { Archive, GitCompare, Info, LogOut, User, TrendingUp, CheckCircle2, XCircle, Bot, Radio, Zap, Pencil, Check, X as XIcon, KeyRound } from "lucide-react";
import { formatDate } from "../lib/format";
import { toast } from "sonner";

export default function PilotPage() {
  const { user, logout } = useAuth();
  const { favs } = useFavorites();
  const [stats, setStats] = useState(null);
  const [droid, setDroid] = useState(null);
  const [editCS, setEditCS] = useState(false);
  const [csDraft, setCsDraft] = useState("");
  const [csBusy, setCsBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get("/polls/stats/me")
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
    api.get("/droid/stats")
      .then(r => setDroid(r.data))
      .catch(() => setDroid(null));
  }, [user]);

  const saveCallsign = async () => {
    const cs = csDraft.trim();
    if (cs === (user?.call_sign || "")) { setEditCS(false); return; }
    setCsBusy(true);
    try {
      const r = await api.patch("/auth/me", { call_sign: cs });
      // Locally patch the user so all screens refresh; a full /me refetch would be
      // safer but AuthContext holds the setter — reload keeps things simple.
      user.call_sign = r.data.call_sign;
      toast.success("CALLSIGN UPDATED");
      setEditCS(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "UPDATE FAILED");
    } finally { setCsBusy(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("PASSWORD MUST BE 6+ CHARS"); return; }
    if (pw1 !== pw2)    { toast.error("PASSWORDS DO NOT MATCH"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw1 });
      toast.success("PASSWORD UPDATED");
      setPw1(""); setPw2(""); setPwOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "UPDATE FAILED");
    } finally { setPwBusy(false); }
  };

  const doLogout = () => {
    // Navigate to /news synchronously; AuthContext.logout defers user=null to
    // a microtask, so PilotPage unmounts before ProtectedRoute sees anon.
    nav("/news", { replace: true });
    logout();
  };

  return (
    <div data-testid="page-pilot">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan">CHANNEL // 006</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">PILOT PROFILE</h1>
      </div>

      {/* Identity card */}
      <div className="sat-surface sat-brackets p-6 md:p-8 mb-6" data-testid="pilot-identity">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-20 h-20 border-2 border-[color:var(--sat-cyan)] flex items-center justify-center sat-box-glow">
            <User size={36} className="sat-cyan" />
          </div>
          <div className="flex-1">
            <div className="sat-label sat-cyan">CALL SIGN</div>
            {editCS ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  autoFocus
                  value={csDraft}
                  onChange={(e) => setCsDraft(e.target.value)}
                  data-testid="pilot-callsign-input"
                  maxLength={32}
                  className="bg-black border border-[color:var(--sat-cyan)] px-2 py-1 font-mono text-lg outline-none w-full max-w-xs"
                />
                <button onClick={saveCallsign} disabled={csBusy || !csDraft.trim()}
                  data-testid="pilot-callsign-save"
                  className="sat-btn sat-btn-solid !py-1.5 !px-2 disabled:opacity-40"><Check size={12} /></button>
                <button onClick={() => setEditCS(false)} data-testid="pilot-callsign-cancel"
                  className="sat-btn !py-1.5 !px-2"><XIcon size={12} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <h2 className="font-display font-bold text-2xl md:text-3xl" data-testid="pilot-callsign">@{user?.call_sign}</h2>
                <button
                  onClick={() => { setCsDraft(user?.call_sign || ""); setEditCS(true); }}
                  data-testid="pilot-callsign-edit"
                  className="sat-btn !py-1 !px-2" title="Edit callsign">
                  <Pencil size={11} />
                </button>
              </div>
            )}
            <div className="sat-label mt-2" data-testid="pilot-email">{user?.email}</div>
            <div className="sat-label mt-1">ACTIVE SINCE · {formatDate(user?.created_at)}</div>
          </div>
          <div className="grid grid-cols-2 gap-4 md:min-w-[180px]">
            <div className="text-center border border-[color:var(--sat-border)] p-3">
              <div className="font-mono font-bold text-2xl sat-cyan sat-glow">{favs.length}</div>
              <div className="sat-label mt-1">ARCHIVED</div>
            </div>
            <div className="text-center border border-[color:var(--sat-border)] p-3">
              <div className="font-mono font-bold text-2xl sat-cyan sat-glow">{favs.filter(f => f.kind === "rocket").length}</div>
              <div className="sat-label mt-1">ROCKETS</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick access */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Link to="/pilot/archive" className="sat-surface sat-brackets p-6 group flex items-center gap-4 hover:sat-box-glow" data-testid="link-archive">
          <Archive size={28} className="sat-cyan" />
          <div className="flex-1">
            <div className="sat-label sat-cyan">DIRECTIVE</div>
            <div className="font-display font-bold text-lg group-hover:text-[color:var(--sat-cyan)] transition-colors">MY ARCHIVE</div>
            <div className="sat-label mt-1">{favs.length} ITEMS ARCHIVED</div>
          </div>
        </Link>
        <Link to="/pilot/compare" className="sat-surface sat-brackets p-6 group flex items-center gap-4 hover:sat-box-glow" data-testid="link-compare">
          <GitCompare size={28} className="sat-cyan" />
          <div className="flex-1">
            <div className="sat-label sat-cyan">DIRECTIVE</div>
            <div className="font-display font-bold text-lg group-hover:text-[color:var(--sat-cyan)] transition-colors">COMPARE ROCKETS</div>
            <div className="sat-label mt-1">SIDE-BY-SIDE VEHICLE ANALYSIS</div>
          </div>
        </Link>
      </div>

      {/* Prediction accuracy */}
      <section className="sat-surface sat-brackets p-6 mb-6" data-testid="pilot-predictions">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={13} className="sat-cyan" />
            <span className="sat-label sat-cyan">PREDICTION LOG</span>
          </div>
          <Link to="/launches" className="sat-label hover:sat-cyan">→ MAKE PREDICTIONS</Link>
        </div>
        {!stats || stats.total === 0 ? (
          <div className="text-sm text-[color:var(--sat-text-dim)]">
            No predictions cast yet. Head to <Link to="/launches" className="sat-cyan hover:underline">Launches → POLLS</Link> and place your first bet on an upcoming mission.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="stat-total">{stats.total}</div>
                <div className="sat-label mt-1">TOTAL VOTES</div>
              </div>
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="stat-resolved">{stats.resolved}</div>
                <div className="sat-label mt-1">RESOLVED</div>
              </div>
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="stat-correct">{stats.correct}</div>
                <div className="sat-label mt-1">CORRECT</div>
              </div>
              <div className="text-center border border-[color:var(--sat-cyan)] p-3 sat-box-glow">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="stat-accuracy">
                  {stats.accuracy == null ? "—" : `${stats.accuracy}%`}
                </div>
                <div className="sat-label mt-1">ACCURACY</div>
              </div>
            </div>
            {stats.breakdown && stats.breakdown.length > 0 && (
              <div className="mt-4">
                <div className="sat-label text-[color:var(--sat-text-dim)] mb-2">RECENT RESULTS</div>
                <ul className="space-y-1.5" data-testid="stat-breakdown">
                  {stats.breakdown.slice(0, 5).map(b => (
                    <li key={b.poll_id}
                        className="flex items-center gap-2 text-[11px] font-mono py-1.5 px-2 border border-[color:var(--sat-border)]">
                      {b.correct
                        ? <CheckCircle2 size={12} className="sat-cyan flex-shrink-0" />
                        : <XCircle size={12} className="text-[color:var(--sat-live)] flex-shrink-0" />}
                      <Link to={`/launches/${b.launch_id}`} className="flex-1 truncate hover:sat-cyan">
                        {b.launch_name}
                      </Link>
                      <span className={b.correct ? "sat-cyan" : "text-[color:var(--sat-live)]"}>
                        {b.correct ? "✓" : "✗"} {b.your_vote.toUpperCase()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {/* R2-A2 telemetry card */}
      <section className="sat-surface sat-brackets p-6 mb-6" data-testid="r2a2-telemetry">
        <div className="sat-label sat-cyan mb-4 flex items-center gap-2">
          <Bot size={12} /> R2-A2 TELEMETRY
        </div>
        {!droid ? (
          <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid="r2a2-telemetry-loading">
            AWAITING UPLINK…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center border border-[color:var(--sat-cyan)] p-3 sat-box-glow">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="r2a2-stat-mine">
                  {droid.my_total}
                </div>
                <div className="sat-label mt-1">MY TRANSMISSIONS</div>
              </div>
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="r2a2-stat-days">
                  {droid.days_active_30d}
                </div>
                <div className="sat-label mt-1">DAYS ACTIVE (30D)</div>
              </div>
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="r2a2-stat-global-total">
                  {droid.global_total}
                </div>
                <div className="sat-label mt-1">FLEET TRANSMISSIONS</div>
              </div>
              <div className="text-center border border-[color:var(--sat-border)] p-3">
                <div className="font-mono font-bold text-2xl sat-cyan sat-glow" data-testid="r2a2-stat-global-users">
                  {droid.global_users}
                </div>
                <div className="sat-label mt-1">PILOTS UPLINKED</div>
              </div>
            </div>
            {droid.my_top_keywords && droid.my_top_keywords.length > 0 && (
              <div className="mt-4">
                <div className="sat-label text-[color:var(--sat-text-dim)] mb-2 flex items-center gap-1">
                  <Zap size={11} /> YOUR TOP KEYWORDS
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="r2a2-top-keywords">
                  {droid.my_top_keywords.map(k => (
                    <span key={k.keyword}
                      data-testid={`r2a2-keyword-${k.keyword}`}
                      className="border border-[color:var(--sat-cyan)]/50 sat-cyan font-mono text-[11px] px-2 py-1">
                      {k.keyword.toUpperCase()} · {k.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {droid.my_total === 0 && (
              <div className="mt-4 sat-label text-[color:var(--sat-text-dim)]" data-testid="r2a2-telemetry-empty">
                <Radio size={11} className="inline mr-1" /> NO TRANSMISSIONS YET — TAP THE R2-A2 BUBBLE TO START.
              </div>
            )}
          </>
        )}
      </section>

      {/* Academic AstronAtlas — cross-brand */}
      <section className="sat-surface sat-brackets p-4 mb-6 border-2 border-[color:var(--sat-cyan)]/40" data-testid="academic-astro-link">
        <a href="https://astronacademics.com" target="_blank" rel="noopener noreferrer"
           className="flex items-start gap-3 group"
           data-testid="academic-astro-anchor">
          <div className="w-10 h-10 flex-shrink-0 border border-[color:var(--sat-cyan)] flex items-center justify-center sat-cyan text-lg font-display font-bold group-hover:bg-[color:var(--sat-cyan)] group-hover:text-black transition-colors">
            A
          </div>
          <div className="flex-1">
            <div className="sat-label sat-cyan mb-0.5 flex items-center gap-2">
              ACADEMIC ASTRONATLAS
              <span className="text-[9px] px-1.5 py-0.5 border border-[color:var(--sat-cyan)] normal-case">extension</span>
            </div>
            <div className="font-display font-bold text-lg leading-tight">
              For students — study companion powered by the AstronAtlas brand.
            </div>
            <div className="sat-label text-[color:var(--sat-text-dim)] mt-1 group-hover:sat-cyan transition-colors">
              astronacademics.com →
            </div>
          </div>
        </a>
      </section>

      {/* About */}
      <section className="sat-surface p-6 mb-6" data-testid="about-astronatlas">
        <div className="sat-label sat-cyan mb-3 flex items-center gap-2"><Info size={12} /> ABOUT ASTRONATLAS</div>
        <p className="text-sm text-[color:var(--sat-text-dim)] leading-relaxed">
          ASTRONATLAS is a mission-control transmission relay for space enthusiasts, aggregating dispatches from launch pads worldwide.
          Track every mission, catalogue every vehicle, archive every milestone.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
          <div>
            <div className="sat-label sat-cyan mb-2">CAPABILITIES</div>
            <ul className="text-sm space-y-1 text-[color:var(--sat-text-dim)] font-mono">
              <li>▸ REAL-TIME SPACE TRANSMISSIONS</li>
              <li>▸ UPCOMING &amp; PAST LAUNCH TRACKING</li>
              <li>▸ COMPREHENSIVE ROCKET DATABASE</li>
              <li>▸ SAVE &amp; ARCHIVE FAVORITES</li>
            </ul>
          </div>
          <div>
            <div className="sat-label sat-cyan mb-2">DATA SOURCES</div>
            <ul className="text-sm space-y-1 text-[color:var(--sat-text-dim)] font-mono">
              <li>▸ SPACEFLIGHT NEWS API</li>
              <li>▸ THE SPACE DEVS API</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-3">
        <button onClick={() => setPwOpen(v => !v)} className="sat-btn" data-testid="pilot-password-toggle">
          <KeyRound size={13} /> {pwOpen ? "CANCEL PASSWORD RESET" : "CHANGE PASSWORD"}
        </button>
        <button onClick={doLogout} className="sat-btn sat-btn-danger" data-testid="logout-btn">
          <LogOut size={13} /> TERMINATE SESSION
        </button>
      </div>

      {pwOpen && (
        <form onSubmit={changePw} className="sat-surface sat-brackets p-6 mt-4 max-w-md space-y-3" data-testid="pilot-password-form">
          <div className="sat-label sat-cyan flex items-center gap-2"><KeyRound size={12} /> ROTATE ACCESS CODE</div>
          <p className="sat-label text-[color:var(--sat-text-dim)]">
            Your current session confirms your identity — you do not need to enter the old password.
          </p>
          <input
            type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
            placeholder="NEW PASSWORD (6+ CHARS)" minLength={6} required
            data-testid="pilot-password-new"
            className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm"
          />
          <input
            type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
            placeholder="CONFIRM NEW PASSWORD" minLength={6} required
            data-testid="pilot-password-confirm"
            className="w-full bg-black border border-[color:var(--sat-border)] focus:border-[color:var(--sat-cyan)] outline-none px-3 py-2 font-mono text-sm"
          />
          <button type="submit" disabled={pwBusy} data-testid="pilot-password-submit"
            className="sat-btn sat-btn-solid disabled:opacity-40">
            {pwBusy ? "UPDATING…" : "UPDATE PASSWORD"}
          </button>
        </form>
      )}
    </div>
  );
}
